/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the `Form`
 * + `FormFooter` shell every FORM screen in this migration uses) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (edit
 * mode — the page-with-back-button + FormGroup field layout).
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useState } from "react";
import { useLoaderData, useNavigate, useOutletContext, type LoaderFunctionArgs } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import {
  PiIdentificationBadgeDuotone as DetailsIcon,
  PiImageDuotone as ImageIcon,
  PiLockDuotone as Lock,
  PiToggleRightDuotone as StatusIcon,
  PiXBold as RemoveIcon,
} from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import Upload from "@core/ui/upload";
import { PageHeader, ErrorSurface, Field, FormGroup, Form } from "@steelyard/theme";
import { imageUrl, useFileUpload, type UploadedFile } from "@steelyard/common";
import { byKey, deactivateBrand, getBrand, getBrandBySlug, reactivateBrand, updateBrand } from "../../../lib/services/catalog";
import { can } from "../../../lib/permissions";
import { formCard, formCardFooter, formSections } from "../../../lib/form-layout";
import { SectionTitle } from "../../../components/section-title";
import type { AccountSummary, PermissionState } from "../../../types";
import { ActionIcon, Text } from "rizzui";
import { RecordSummary } from "../../../components/detail-parts";
import { StatusSwitch } from "../../../components/status-switch";
import { UploadDropHint, UploadBusyHint } from "../../../components/upload-drop-hint";
import { useGoBack } from "../../../lib/navigation";
import { changedSlug, SLUG_HINT, slugSchema } from "../../../lib/slug";
import { brandPath } from "../../../lib/paths";

/** Ported from apps/admin/app/(app)/catalogue/brands/[id]/page.tsx. */
export async function brandDetailLoader({ params, request }: LoaderFunctionArgs) {
  const brand = await byKey(request, params.slug!, getBrand, getBrandBySlug, brandPath);
  if (!brand) throw new Error("The API returned no body for this brand.");
  return { brand };
}

const brandEditSchema = z.object({
  name: z.string().trim().min(1, "Required").max(200),
  slug: slugSchema,
  isActive: z.boolean(),
});
type BrandEditValues = z.infer<typeof brandEditSchema>;

export default function BrandDetail() {
  const { brand } = useLoaderData() as Awaited<ReturnType<typeof brandDetailLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  // Back is a history step, so the list returns with whatever filter it had; with no history the
  // bare list path already opens on the default Active view — nothing to carry in this URL.
  const goBack = useGoBack("/catalogue/brands");
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const [logoError, setLogoError] = useState<unknown>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  // Seeded from the persisted logo so a reload shows the existing image, same idiom as
  // products/Detail.tsx's `imageHash`/`uploadedImage` split.
  const [logoHash, setLogoHash] = useState<string | null>(brand.logoHash);
  const [uploadedLogo, setUploadedLogo] = useState<UploadedFile | null>(null);
  const logo = useFileUpload("image", {
    onUploaded: async (result) => {
      setLogoError(null);
      setUploadedLogo(result);
      setLogoHash(result.hash);
      await updateBrand(brand.id, { name: brand.name, logoHash: result.hash });
      toast.success("Logo uploaded");
    },
    onError: () => toast.error("Could not upload the logo"),
  });
  // `isUploadingLogo`/`logoError` now cover only the remove; the upload's own are `logo.busy`/`logo.error`.
  const logoBusy = logo.busy || isUploadingLogo;

  if (!can(permissionState, "catalogue.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view this brand" />;
  }
  const canWrite = can(permissionState, "catalogue.administer");

  async function handleRemoveLogo() {
    setLogoError(null);
    setIsUploadingLogo(true);
    try {
      await updateBrand(brand.id, { name: brand.name, logoHash: null });
      setLogoHash(null);
      setUploadedLogo(null);
      toast.success("Logo removed");
    } catch (err) {
      setLogoError(err);
      toast.error("Could not remove the logo");
    } finally {
      setIsUploadingLogo(false);
    }
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title={brand.name}
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Catalogue" }, { name: "Brands", href: "/catalogue/brands" }, { name: brand.name }]}
      />
      {/* A state badge is not part of a page's name. The status itself is edited in the form below. */}
      <RecordSummary code={brand.code} />

      {error ? <ErrorSurface error={error} action="save this brand" /> : null}

      <Form<BrandEditValues>
        validationSchema={brandEditSchema}
        useFormProps={{ defaultValues: { name: brand.name, slug: brand.slug, isActive: brand.isActive } }}
        className={formCard}
        onSubmit={async (values) => {
          setError(null);
          // Set once the update has saved a new slug: from then on the old slug's URL reads nothing.
          let savedSlug: string | undefined;
          // A slug change makes the new address the record's only one (there is no slug history), so
          // the page moves there, replacing the old entry rather than leaving it in history.
          const moveTo = (slug: string) => navigate(brandPath(slug), { replace: true });
          try {
            // Includes the current logoHash so a name-only edit here doesn't clobber a logo the
            // upload block below already persisted separately.
            const slug = changedSlug(values.slug, brand.slug);
            await updateBrand(brand.id, { name: values.name, logoHash, slug });
            savedSlug = slug;
            // The status is its own call (deactivate never deletes — FR-007), made only when the box changed.
            if (values.isActive !== brand.isActive) await (values.isActive ? reactivateBrand : deactivateBrand)(brand.id);
            toast.success("Brand saved");
            if (slug) moveTo(slug);
            else goBack();
          } catch (err) {
            setError(err);
            toast.error("Could not save the brand");
            // The update went through and only the status call failed: show that on the new address.
            if (savedSlug) moveTo(savedSlug);
          }
        }}
      >
        {({ register, formState: { errors, isSubmitting } }) => (
          <>
            <div className={formSections}>
              <FormGroup title={<SectionTitle icon={DetailsIcon}>Details</SectionTitle>} description="The code is fixed at creation; the name and URL slug can change.">
                <Field label="Name" required maxLength={200} disabled={!canWrite} error={errors.name?.message} {...register("name")} />
                <Field
                  label="Code"
                  name="code"
                  defaultValue={brand.code}
                  readOnly
                  disabled
                  className="font-mono"
                  hint={
                    <span className="flex items-center gap-1.5">
                      <Lock aria-hidden className="size-3" />
                      Permanent.
                    </span>
                  }
                />
                <Field
                  label="URL slug"
                  maxLength={200}
                  required
                  className="font-mono"
                  disabled={!canWrite}
                  hint={SLUG_HINT}
                  error={errors.slug?.message}
                  {...register("slug")}
                />
              </FormGroup>

              {canWrite ? (
                <FormGroup title={<SectionTitle icon={ImageIcon}>Logo</SectionTitle>} description="Optional. Uploaded as a real file, not linked.">
                  <div className="col-span-full flex flex-wrap items-center gap-4">
                    <Upload
                      accept="img"
                      multiple={false}
                      disabled={logoBusy}
                      onChange={logo.onChange}
                      aria-label="Brand logo file"
                      placeholderText={logoBusy ? <UploadBusyHint label="Uploading…" /> : <UploadDropHint />}
                      iconClassName="size-8 shrink-0"
                      className="p-4"
                      wrapperClassName="w-full max-w-xs"
                    />
                    {logoHash ? (
                      <div className="flex flex-col items-start gap-1.5">
                        <div className="relative">
                          <img src={imageUrl(logoHash)} alt={`${brand.name} — logo`} className="h-20 w-20 rounded-lg border border-muted object-cover" />
                          <ActionIcon
                            type="button"
                            size="sm"
                            variant="solid"
                            color="danger"
                            className="absolute -right-2 -top-2"
                            disabled={logoBusy}
                            aria-label="Remove this logo"
                            onClick={handleRemoveLogo}
                          >
                            <RemoveIcon aria-hidden className="size-3.5" />
                          </ActionIcon>
                        </div>
                        {uploadedLogo ? (
                          <Text className="text-xs text-gray-500">
                            {uploadedLogo.sizeBytes.toLocaleString()} bytes · {uploadedLogo.contentType}
                          </Text>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  {logoError ?? logo.error ? <ErrorSurface error={logoError ?? logo.error} action="upload this logo" /> : null}
                </FormGroup>
              ) : null}

              <FormGroup title={<SectionTitle icon={StatusIcon}>Status</SectionTitle>} description="Whether new products can be filed under this brand.">
                <StatusSwitch
                  className="@xl:col-span-2"
                  disabled={!canWrite}
                  activeText="Offered on new products."
                  inactiveText="Not offered on new products. Nothing is deleted — existing products, prices and past orders keep referring to it."
                  {...register("isActive")}
                />
              </FormGroup>
            </div>

            {canWrite ? (
              <FormFooter
                className={formCardFooter}
                submitBtnText={isSubmitting ? "Saving…" : "Save changes"}
                isLoading={isSubmitting}
                altBtnText="Cancel"
                handleAltBtn={goBack}
              />
            ) : null}
          </>
        )}
      </Form>
    </div>
  );
}
